import React, { useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Chip,
    InputAdornment,
    Alert,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CreateFp1ShipmentDevice, Fp1BenchImportPayload, Fp1DeviceType } from '../fp1.types';
import { createFp1Shipment } from '../fp1.api';
import { parseFp1BenchImportFile } from '../utils/fp1BenchSpreadsheetImport';

function benchImportHasError(bench?: Fp1BenchImportPayload): boolean {
    const e = bench?.error_message?.trim();
    return !!e;
}

function parseDollarsToCents(value: string): number {
    const n = parseFloat(value);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const STEPS = ['Shipment Info', 'Register Devices', 'Review & Submit'];

const TYPE_LABELS: Record<Fp1DeviceType, string> = {
    fp1: 'FP1',
    fp1_gateway: 'Gateway (FP1)',
};

const Fp1ReceiveShipmentDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
    const benchFileInputRef = useRef<HTMLInputElement>(null);
    const [activeStep, setActiveStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Step 1: Shipment info
    const [supplierName, setSupplierName] = useState('');
    const [poNumber, setPoNumber] = useState('');
    const [receivedAt, setReceivedAt] = useState(new Date().toISOString().split('T')[0]);
    const [shipmentNotes, setShipmentNotes] = useState('');
    /** PCB / hardware revision for every device in this shipment (stored once; cannot change after create). */
    const [shipmentHardwareVersion, setShipmentHardwareVersion] = useState('');

    // Step 2: Devices
    const [devices, setDevices] = useState<CreateFp1ShipmentDevice[]>([]);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkSerials, setBulkSerials] = useState('');
    const [bulkType, setBulkType] = useState<Fp1DeviceType>('fp1');
    /** Applied to bulk-pasted rows that have no bench CSV firmware. */
    const [bulkDefaultFirmware, setBulkDefaultFirmware] = useState('');
    /** Applies to every device row (same unit cost for the whole shipment). */
    const [shipmentDefaultUnitCost, setShipmentDefaultUnitCost] = useState('');

    const defaultUnitCostCents = () => parseDollarsToCents(shipmentDefaultUnitCost);

    const handleShipmentDefaultUnitCostChange = (value: string) => {
        setShipmentDefaultUnitCost(value);
        const cents = parseDollarsToCents(value);
        setDevices((prev) => prev.map((d) => ({ ...d, unit_cost_cents: cents })));
    };

    const handleAddDevice = () => {
        setDevices([
            ...devices,
            {
                serial_number: '',
                device_type: 'fp1',
                model: '',
                unit_cost_cents: defaultUnitCostCents(),
            },
        ]);
    };

    const handleRemoveDevice = (idx: number) => {
        setDevices(devices.filter((_, i) => i !== idx));
    };

    const handleDeviceChange = (idx: number, field: keyof CreateFp1ShipmentDevice, value: any) => {
        const updated = [...devices];
        updated[idx] = { ...updated[idx], [field]: value };
        setDevices(updated);
    };

    const handleBenchImportClick = () => {
        benchFileInputRef.current?.click();
    };

    const handleBenchImportSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        setError('');
        try {
            const parsed = await parseFp1BenchImportFile(file);
            if (parsed.length === 0) {
                setError(
                    'No device rows found. Expected 12 columns: (1) Date/Time tested, (2) Current without FW, (3) Serial, (4) Firmware flashed, (5) Standby, (6) Live, (7) Charge, (8) VBAT, (9) PMID, (10) IIN, (11) ICHG, (12) Error. Older 11-column and 7–8 column exports are still supported.',
                );
                return;
            }
            const cents = parseDollarsToCents(shipmentDefaultUnitCost);
            setDevices((prev) => {
                const next = [...prev];
                const indexBySerial = new Map(
                    next.map((d, idx) => [d.serial_number.trim().toLowerCase(), idx]),
                );
                for (const row of parsed) {
                    const fw = row.bench_import?.fw_version?.trim();
                    const firmwareFromBench = fw || undefined;
                    const key = row.serial_number.trim().toLowerCase();
                    const existingIdx = indexBySerial.get(key);
                    if (existingIdx !== undefined) {
                        const cur = next[existingIdx];
                        next[existingIdx] = {
                            ...cur,
                            serial_number: row.serial_number.trim(),
                            model: cur.model ?? '',
                            bench_import: row.bench_import,
                            firmware_version:
                                firmwareFromBench ||
                                (cur.firmware_version as string | undefined)?.trim() ||
                                bulkDefaultFirmware.trim() ||
                                undefined,
                            unit_cost_cents: cents > 0 ? cents : cur.unit_cost_cents,
                        };
                    } else {
                        indexBySerial.set(key, next.length);
                        next.push({
                            serial_number: row.serial_number.trim(),
                            device_type: bulkType,
                            model: '',
                            firmware_version:
                                firmwareFromBench || bulkDefaultFirmware.trim() || undefined,
                            unit_cost_cents: cents,
                            bench_import: row.bench_import,
                        });
                    }
                }
                return next;
            });
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to import file';
            setError(msg);
        }
    };

    const handleBulkAdd = () => {
        const serials = bulkSerials
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);

        if (serials.length === 0) return;

        const cents = parseDollarsToCents(shipmentDefaultUnitCost);
        const fw = bulkDefaultFirmware.trim() || undefined;
        const newDevices: CreateFp1ShipmentDevice[] = serials.map((sn) => ({
            serial_number: sn,
            device_type: bulkType,
            model: '',
            firmware_version: fw,
            unit_cost_cents: cents,
        }));

        setDevices([...devices, ...newDevices]);
        setBulkSerials('');
        setBulkMode(false);
    };

    const validateStep1 = () => {
        if (!supplierName.trim()) {
            setError('Supplier name is required');
            return false;
        }
        if (!receivedAt) {
            setError('Received date is required');
            return false;
        }
        if (!shipmentHardwareVersion.trim()) {
            setError('Hardware (PCB) version for this shipment is required (set on step 1).');
            return false;
        }
        setError('');
        return true;
    };

    const validateStep2 = () => {
        if (devices.length === 0) {
            setError('Add at least one device');
            return false;
        }
        const serials = devices.map((d) => d.serial_number.trim().toLowerCase());
        const duplicates = serials.filter((s, i) => s && serials.indexOf(s) !== i);
        if (duplicates.length > 0) {
            setError(`Duplicate serial numbers: ${[...new Set(duplicates)].join(', ')}`);
            return false;
        }
        if (devices.some((d) => !d.serial_number.trim())) {
            setError('All devices must have a serial number');
            return false;
        }
        setError('');
        return true;
    };

    const handleNext = () => {
        if (activeStep === 0 && !validateStep1()) return;
        if (activeStep === 1 && !validateStep2()) return;
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setError('');
        setActiveStep((prev) => prev - 1);
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            await createFp1Shipment({
                supplier_name: supplierName,
                po_number: poNumber || undefined,
                received_at: receivedAt,
                notes: shipmentNotes || undefined,
                default_hardware_version: shipmentHardwareVersion.trim() || undefined,
                devices: devices.map((d) => {
                    const hasBench =
                        d.bench_import &&
                        Object.values(d.bench_import).some(
                            (v) => v != null && String(v).trim() !== '',
                        );
                    const fwFromRow =
                        (typeof d.firmware_version === 'string' && d.firmware_version.trim()) ||
                        d.bench_import?.fw_version?.trim() ||
                        '';
                    return {
                        serial_number: d.serial_number.trim(),
                        device_type: d.device_type,
                        model: d.model.trim(),
                        unit_cost_cents: d.unit_cost_cents || undefined,
                        firmware_version: fwFromRow || undefined,
                        notes: d.notes || undefined,
                        ...(hasBench ? { bench_import: d.bench_import } : {}),
                    };
                }),
            });
            onSuccess();
            handleReset();
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to create shipment';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setSupplierName('');
        setPoNumber('');
        setReceivedAt(new Date().toISOString().split('T')[0]);
        setShipmentNotes('');
        setShipmentHardwareVersion('');
        setDevices([]);
        setBulkMode(false);
        setBulkSerials('');
        setBulkDefaultFirmware('');
        setShipmentDefaultUnitCost('');
        setError('');
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const formatCost = (cents: number) => (cents / 100).toFixed(2);
    const totalCost = devices.reduce((sum, d) => sum + (d.unit_cost_cents || 0), 0);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Receive Shipment
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Step 1: Shipment Info */}
                {activeStep === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            label="Supplier Name *"
                            value={supplierName}
                            onChange={(e) => setSupplierName(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g. Pozyx, DecaWave"
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="PO Number"
                                value={poNumber}
                                onChange={(e) => setPoNumber(e.target.value)}
                                sx={{ flex: 1 }}
                                size="small"
                                placeholder="Purchase order reference"
                            />
                            <TextField
                                label="Date Received *"
                                type="date"
                                value={receivedAt}
                                onChange={(e) => setReceivedAt(e.target.value)}
                                sx={{ flex: 1 }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <TextField
                            label="Notes"
                            value={shipmentNotes}
                            onChange={(e) => setShipmentNotes(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            placeholder="e.g. Partial shipment, remaining 10 trackers back-ordered"
                        />
                        <TextField
                            label="Hardware (PCB) version — entire shipment *"
                            value={shipmentHardwareVersion}
                            onChange={(e) => setShipmentHardwareVersion(e.target.value)}
                            fullWidth
                            size="small"
                            required
                            placeholder="e.g. Rev C, PCB v2.1"
                            helperText="Stored on every device in this shipment and cannot be changed after the shipment is created. Firmware is tracked separately (from bench CSV or per device below)."
                        />
                    </Box>
                )}

                {/* Step 2: Register Devices */}
                {activeStep === 1 && (
                    <Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                            <TextField
                                size="small"
                                label="Unit cost for all devices ($)"
                                value={shipmentDefaultUnitCost}
                                onChange={(e) =>
                                    handleShipmentDefaultUnitCostChange(e.target.value)
                                }
                                sx={{ maxWidth: 260 }}
                                placeholder="e.g. 129.99"
                                helperText="Applies to every row below, including new rows from bulk paste or CSV/Excel import."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Devices ({devices.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <input
                                    ref={benchFileInputRef}
                                    type="file"
                                    accept=".csv,.txt,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    style={{ display: 'none' }}
                                    onChange={handleBenchImportSelected}
                                />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ContentPasteIcon />}
                                    onClick={() => setBulkMode(!bulkMode)}
                                >
                                    Bulk Paste
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<UploadFileIcon />}
                                    onClick={handleBenchImportClick}
                                >
                                    Import CSV / Excel
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddDevice}
                                >
                                    Add Device
                                </Button>
                            </Box>
                        </Box>

                        {bulkMode && (
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}
                            >
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Paste serial numbers (one per line)
                                </Typography>
                                <TextField
                                    multiline
                                    rows={4}
                                    fullWidth
                                    size="small"
                                    value={bulkSerials}
                                    onChange={(e) => setBulkSerials(e.target.value)}
                                    placeholder={'SN-001\nSN-002\nSN-003'}
                                    sx={{ mb: 1.5 }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={bulkType}
                                            onChange={(e) =>
                                                setBulkType(e.target.value as Fp1DeviceType)
                                            }
                                            label="Type"
                                        >
                                            <MenuItem value="fp1">FP1</MenuItem>
                                            <MenuItem value="fp1_gateway">Gateway (FP1)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Default firmware (bulk paste only)"
                                        value={bulkDefaultFirmware}
                                        onChange={(e) => setBulkDefaultFirmware(e.target.value)}
                                        placeholder="Optional if rows have no bench CSV"
                                        sx={{ minWidth: 220 }}
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleBulkAdd}
                                    >
                                        Add {bulkSerials.split('\n').filter((s) => s.trim()).length}{' '}
                                        Devices
                                    </Button>
                                </Box>
                            </Paper>
                        )}

                        {devices.length === 0 && !bulkMode && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    borderStyle: 'dashed',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No devices added yet. Use &ldquo;Add Device&rdquo;, &ldquo;Bulk
                                    Paste&rdquo;, or &ldquo;Import CSV / Excel&rdquo;. Enter
                                    hardware (PCB) version on step 1 first. Factory export is 12
                                    columns with firmware in column 4 and error in column 12; older
                                    layouts are still accepted.
                                </Typography>
                            </Paper>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {devices.map((device, idx) => (
                                <Paper
                                    key={idx}
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'center',
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        label="Serial Number *"
                                        value={device.serial_number}
                                        onChange={(e) =>
                                            handleDeviceChange(idx, 'serial_number', e.target.value)
                                        }
                                        sx={{ flex: 2 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={device.device_type}
                                            onChange={(e) =>
                                                handleDeviceChange(
                                                    idx,
                                                    'device_type',
                                                    e.target.value,
                                                )
                                            }
                                            label="Type"
                                        >
                                            <MenuItem value="fp1">FP1</MenuItem>
                                            <MenuItem value="fp1_gateway">Gateway (FP1)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        label="Firmware"
                                        value={
                                            (device.firmware_version as string | undefined) ??
                                            device.bench_import?.fw_version ??
                                            ''
                                        }
                                        onChange={(e) =>
                                            handleDeviceChange(
                                                idx,
                                                'firmware_version',
                                                e.target.value,
                                            )
                                        }
                                        sx={{ flex: 1 }}
                                        placeholder="From CSV column 4 when imported"
                                    />
                                    <TextField
                                        size="small"
                                        label="Unit Cost"
                                        type="number"
                                        value={
                                            device.unit_cost_cents
                                                ? (device.unit_cost_cents / 100).toFixed(2)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            handleDeviceChange(
                                                idx,
                                                'unit_cost_cents',
                                                Math.round(parseFloat(e.target.value || '0') * 100),
                                            )
                                        }
                                        sx={{ width: 110 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {device.bench_import &&
                                            Object.values(device.bench_import).some(
                                                (v) => v != null && String(v).trim() !== '',
                                            ) && (
                                                <Chip
                                                    label="Bench CSV"
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.65rem', height: 22 }}
                                                />
                                            )}
                                        {benchImportHasError(device.bench_import) && (
                                            <Chip
                                                label="Defective (bench error)"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 22 }}
                                            />
                                        )}
                                        <IconButton
                                            onClick={() => handleRemoveDevice(idx)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Step 3: Review */}
                {activeStep === 2 && (
                    <Box>
                        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                Shipment Details
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <ReviewItem label="Supplier" value={supplierName} />
                                <ReviewItem label="PO Number" value={poNumber || '--'} />
                                <ReviewItem label="Received" value={receivedAt} />
                                <ReviewItem
                                    label="Total Cost"
                                    value={totalCost > 0 ? `$${formatCost(totalCost)}` : '--'}
                                />
                                <ReviewItem
                                    label="Hardware (PCB) — all devices"
                                    value={shipmentHardwareVersion.trim() || '--'}
                                />
                            </Box>
                            {shipmentNotes && (
                                <Box sx={{ mt: 1.5 }}>
                                    <ReviewItem label="Notes" value={shipmentNotes} />
                                </Box>
                            )}
                        </Paper>

                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Devices ({devices.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {(['fp1', 'fp1_gateway'] as Fp1DeviceType[]).map((type) => {
                                const count = devices.filter((d) => d.device_type === type).length;
                                if (count === 0) return null;
                                return (
                                    <Chip
                                        key={type}
                                        label={`${count} ${TYPE_LABELS[type]}${
                                            count > 1 ? 's' : ''
                                        }`}
                                        size="small"
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{ maxHeight: 200, overflow: 'auto', borderRadius: 1.5 }}
                        >
                            {devices.map((d, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        px: 2,
                                        py: 0.75,
                                        borderBottom:
                                            idx < devices.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                                    >
                                        {d.serial_number}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {TYPE_LABELS[d.device_type]}
                                        </Typography>
                                        {(typeof d.firmware_version === 'string' &&
                                            d.firmware_version.trim()) ||
                                        d.bench_import?.fw_version?.trim() ? (
                                            <Typography variant="body2" color="text.secondary">
                                                FW:{' '}
                                                {(typeof d.firmware_version === 'string' &&
                                                    d.firmware_version.trim()) ||
                                                    d.bench_import?.fw_version?.trim()}
                                            </Typography>
                                        ) : null}
                                        {(d.unit_cost_cents || 0) > 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                ${formatCost(d.unit_cost_cents!)}
                                            </Typography>
                                        )}
                                        {benchImportHasError(d.bench_import) && (
                                            <Chip
                                                label="Defective at receipt"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 22 }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Paper>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Box sx={{ flex: 1 }} />
                {activeStep > 0 && (
                    <Button onClick={handleBack} disabled={saving}>
                        Back
                    </Button>
                )}
                {activeStep < STEPS.length - 1 ? (
                    <Button variant="contained" onClick={handleNext}>
                        Next
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving}
                        color="primary"
                    >
                        {saving ? (
                            <CircularProgress size={20} />
                        ) : (
                            `Create Shipment (${devices.length} devices)`
                        )}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

const ReviewItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
        </Typography>
    </Box>
);

export default Fp1ReceiveShipmentDialog;
